import type { AssistantContextItem } from "@/lib/use-assistant-context";
import type { FactSkillView } from "./match-result-codec";
import type {
  RankedCandidate,
  RankedCriterion,
  RankSortBy,
  RankThresholds,
  ResultPerson,
} from "./match-result-derive";

const MAX_CONTEXT_VALUE_LENGTH = 1_900;
const MAX_CONTEXT_DESCRIPTION_LENGTH = 190;
// Route + MatchDetailPage + ResultStep consume three of the BFF's 20 AG-UI
// context slots. Keep one slot in reserve for another mounted page component.
const MAX_RANKING_CONTEXT_ITEMS = 16;

function compact(value: string | null | undefined): string {
  return (value ?? "").replace(/\s+/g, " ").trim();
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function conditionLabel(condition: unknown): string {
  if (!condition || typeof condition !== "object") return "unknown criterion";
  const value = condition as Record<string, unknown>;
  const named =
    value.expertise ??
    value.name ??
    value.value ??
    value.languageTag ??
    value.id;
  if (typeof named === "string" && named.trim()) return compact(named);
  if (typeof value.__typename === "string") {
    return value.__typename.replace(/Condition$/, "") || "unknown criterion";
  }
  return "unknown criterion";
}

function criterionEvidence(
  criterion: RankedCriterion,
  formatCondition: (condition: unknown) => string,
): string | null {
  if (!criterion.actual && !criterion.aiReason) return null;
  const details = [
    `${criterion.requirement === "MUST" ? "MUST" : "OPTIONAL/KANN"} ${formatCondition(criterion.condition)}`,
    criterion.actual ? `actual: ${compact(criterion.actual)}` : null,
    criterion.aiReason ? `AI reason: ${compact(criterion.aiReason)}` : null,
  ].filter(Boolean);
  return details.join(", ");
}

function fulfillmentSum(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

function limited(value: string): string {
  if (value.length <= MAX_CONTEXT_VALUE_LENGTH) return value;
  return `${value.slice(0, MAX_CONTEXT_VALUE_LENGTH - 1).trimEnd()}…`;
}

function limitedDescription(value: string): string {
  if (value.length <= MAX_CONTEXT_DESCRIPTION_LENGTH) return value;
  return `${value.slice(0, MAX_CONTEXT_DESCRIPTION_LENGTH - 1).trimEnd()}…`;
}

function shortText(value: string | null | undefined, maxLength = 240): string {
  const normalized = compact(value);
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
}

function packRecords(records: string[]): string[] {
  const chunks: string[] = [];
  let current = "";
  for (const rawRecord of records) {
    const record = limited(rawRecord);
    const next = current ? `${current}\n${record}` : record;
    if (next.length <= MAX_CONTEXT_VALUE_LENGTH) {
      current = next;
      continue;
    }
    if (current) chunks.push(current);
    current = record;
  }
  if (current) chunks.push(current);
  return chunks;
}

function sortLabel(sortBy: RankSortBy): string {
  if (sortBy === "power") return "power fit";
  if (sortBy === "requirements") return "optional-requirements fit";
  return "holistic AI suitability";
}

export function buildRankingAssistantContext({
  ranked,
  persons,
  factSkill,
  thresholds,
  sortBy,
  formatCondition = conditionLabel,
  focusedPersonIds = new Set<string>(),
}: {
  ranked: RankedCandidate[];
  persons: ResultPerson[];
  factSkill: FactSkillView | null;
  thresholds: RankThresholds;
  sortBy: RankSortBy;
  formatCondition?: (condition: unknown) => string;
  focusedPersonIds?: ReadonlySet<string>;
}): AssistantContextItem[] {
  if (ranked.length === 0) return [];

  const counts = ranked.reduce(
    (acc, candidate) => {
      acc[candidate.cluster] += 1;
      return acc;
    },
    { A: 0, B: 0, C: 0, UNQUALIFIED: 0 },
  );
  const context: AssistantContextItem[] = [
    {
      description: "the live candidate-ranking result currently visible",
      value:
        `${ranked.length} candidates; sorted by ${sortLabel(sortBy)}; ` +
        `thresholds A >= ${percent(thresholds.a)}, B >= ${percent(thresholds.b)}, ` +
        `C >= ${percent(thresholds.c ?? 0)}; distribution: A ${counts.A}, B ${counts.B}, ` +
        `C ${counts.C}, below C or failed MUST ${counts.UNQUALIFIED}. ` +
        "Candidate records below cover the complete ranking in its current visible order. " +
        "Each candidate record includes the stable person ID; use that ID with the person-information tool when full profile details are needed. " +
        "Requirements fit is the OPTIONAL/KANN score; all MUST criteria are a hard qualification gate. " +
        "The KANN badge numerator is the sum of fulfillment degrees and may be fractional; the fully fulfilled list below is the exact criterion count.",
    },
  ];

  const assessmentByPerson = new Map(
    (factSkill?.teamMembers ?? []).map((member) => [member.personId, member.aiAssessment]),
  );
  const personById = new Map(persons.map((person) => [person.id, person]));

  const metrics: string[] = [];
  const criteriaDetails: Array<{ personId: string; value: string }> = [];

  for (const [index, candidate] of ranked.entries()) {
    const assessment = assessmentByPerson.get(candidate.personId);
    const person = personById.get(candidate.personId);
    const must = candidate.criteria.filter((criterion) => criterion.requirement === "MUST");
    const optional = candidate.criteria.filter((criterion) => criterion.requirement !== "MUST");
    const fullyFulfilledOptional = optional.filter((criterion) => criterion.fulfillment >= 1);
    const partiallyFulfilledOptional = optional.filter(
      (criterion) => criterion.fulfillment > 0 && criterion.fulfillment < 1,
    );
    const unfulfilledOptional = optional.filter((criterion) => criterion.fulfillment <= 0);
    const fulfilledMust = must.filter((criterion) => criterion.fulfillment >= 1);
    const failedMust = must.filter((criterion) => criterion.fulfillment < 1);
    const labels = (criteria: RankedCriterion[]) =>
      criteria.map((criterion) => compact(formatCondition(criterion.condition))).join(" | ") || "none";
    const headline = [
      `position ${index + 1}`,
      `candidate ${candidate.personName}`,
      `person ID ${candidate.personId}`,
      person?.profession ? `profession ${compact(person.profession)}` : null,
      `cluster ${candidate.cluster}`,
      candidate.qualified ? "MUST gate passed" : "MUST gate failed",
      `MUST ${candidate.mustMet}/${candidate.mustTotal}`,
      `OPTIONAL/KANN fulfillment ${fulfillmentSum(candidate.kannSum)}/${candidate.kannTotal} (${percent(candidate.score)})`,
      `fully fulfilled OPTIONAL/KANN ${fullyFulfilledOptional.length}`,
      `partially fulfilled OPTIONAL/KANN ${partiallyFulfilledOptional.length}`,
      `not fulfilled OPTIONAL/KANN ${unfulfilledOptional.length}`,
      candidate.powerFit == null ? null : `power fit ${Math.round(candidate.powerFit)}%`,
      candidate.suitabilityScore == null
        ? null
        : `holistic AI suitability ${percent(candidate.suitabilityScore)}`,
      assessment?.fazit ? `AI conclusion: ${shortText(assessment.fazit)}` : null,
    ].filter(Boolean);
    const criterionStatus = [
      `fully fulfilled OPTIONAL/KANN (${fullyFulfilledOptional.length}): ${labels(fullyFulfilledOptional)}`,
      partiallyFulfilledOptional.length
        ? `partially fulfilled OPTIONAL/KANN (${partiallyFulfilledOptional.length}): ${partiallyFulfilledOptional
            .map(
              (criterion) =>
                `${compact(formatCondition(criterion.condition))} ${percent(criterion.fulfillment)}`,
            )
            .join(" | ")}`
        : null,
      `not fulfilled OPTIONAL/KANN (${unfulfilledOptional.length}): ${labels(unfulfilledOptional)}`,
      must.length ? `fulfilled MUST (${fulfilledMust.length}): ${labels(fulfilledMust)}` : null,
      failedMust.length ? `failed MUST (${failedMust.length}): ${labels(failedMust)}` : null,
    ].filter(Boolean);
    metrics.push(headline.join("; "));
    criteriaDetails.push({
      personId: candidate.personId,
      value: limited(
        [
          `candidate ${candidate.personName}`,
          `person ID ${candidate.personId}`,
          person?.profession ? `profession ${compact(person.profession)}` : null,
          ...criterionStatus,
          assessment?.positive ? `AI positives: ${shortText(assessment.positive)}` : null,
          assessment?.negative ? `AI concerns: ${shortText(assessment.negative)}` : null,
          assessment?.interviewQuestions?.length
            ? `suggested interview questions: ${assessment.interviewQuestions
                .map((question) => shortText(question, 120))
                .join(" | ")}`
            : null,
          (() => {
            const evidence = candidate.criteria
              .map((criterion) => criterionEvidence(criterion, formatCondition))
              .filter(Boolean);
            return evidence.length ? `criterion evidence: ${evidence.join(" | ")}` : null;
          })(),
        ]
          .filter(Boolean)
          .join(". "),
      ),
    });
  }

  const metricChunks = packRecords(metrics);
  for (const [index, value] of metricChunks.entries()) {
    if (context.length >= MAX_RANKING_CONTEXT_ITEMS) break;
    context.push({
      description: limitedDescription(
        `candidate ranking metrics, part ${index + 1} of ${metricChunks.length}`,
      ),
      value,
    });
  }

  // Expanded candidate rows are literally what the user is looking at, so put
  // their full criterion breakdown first when the finite AG-UI budget fills up.
  const orderedCriteriaDetails = [...criteriaDetails].sort(
    (left, right) =>
      Number(focusedPersonIds.has(right.personId)) - Number(focusedPersonIds.has(left.personId)),
  );
  const detailChunks = packRecords(orderedCriteriaDetails.map((detail) => detail.value));
  for (const [index, value] of detailChunks.entries()) {
    if (context.length >= MAX_RANKING_CONTEXT_ITEMS) break;
    context.push({
      description: limitedDescription(
        `candidate ranking criteria, part ${index + 1} of ${detailChunks.length}`,
      ),
      value,
    });
  }

  return context;
}
